import { useEffect } from 'react';

import { getIdleMs, hasEnteredOnPage, watchReaderActivity } from '../../common/readerActivity';
import reloadDocument from '../../common/reloadDocument';
import { isAnyReloadHeld } from '../../common/reloadHolds';
import { hasUnsavedChanges } from './useRefreshWarning';

export const QUIET_CHECK_INTERVAL_MS = 30 * 1000;

/**
 * Whether a reload now would interrupt nothing: the reader has been away for
 * `quietForMs`, and for that long since `since`; no field they entered
 * something into is still on the page, and they hold no unsaved changes;
 * nothing holds the reload; and the tab is online, since offline a reload
 * shows the browser's error page.
 */
export const isQuietFor = (quietForMs: number, since = 0) =>
  navigator.onLine &&
  !hasUnsavedChanges() &&
  !isAnyReloadHeld() &&
  !hasEnteredOnPage() &&
  Math.min(getIdleMs(), Date.now() - since) >= quietForMs;

interface UseReloadWhenQuietProps {
  /** When the tab came to want a reload, or undefined while it does not; read at each check. */
  getWantedSince: () => number | undefined;
  quietForMs: number;
  /** Claims the reload, or refuses one too soon after the last. */
  claim: () => boolean;
}

/**
 * Reloads a tab that wants it once nobody has used it for a while, for a page
 * nobody attends, such as the roll board on a screen in the foyer, which never
 * changes page. Pass stable functions, or the checks restart on every render.
 */
const useReloadWhenQuiet = ({ getWantedSince, quietForMs, claim }: UseReloadWhenQuietProps) => {
  useEffect(() => {
    const stopWatching = watchReaderActivity();
    const interval = window.setInterval(() => {
      const since = getWantedSince();
      if (since !== undefined && isQuietFor(quietForMs, since) && claim()) {
        reloadDocument();
      }
    }, QUIET_CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(interval);
      stopWatching();
    };
  }, [getWantedSince, quietForMs, claim]);
};

export default useReloadWhenQuiet;
